import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Ban, Eye, Loader2, Pause, Play, Users } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loading, EmptyState } from "@/components/common/Loading";
import { OaReasonDialog } from "@/components/admin/otterassist/OaReasonDialog";

// Otter Assist admin — Assist Detail. A DEDICATED admin surface, deliberately not the member Need
// Detail, so neither side leaks into the other.
//
// Helper messages, delivered-recipient identities and candidate diagnostics are three separate
// DELIBERATE reveals, each requiring a stated reason and each logged as a sensitive view (OAD-126).

function Section({ title, testId, children, action }) {
  return (
    <Card className="p-4" data-testid={testId}>
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-[640]">{title}</h3>
        {action}
      </div>
      <div className="mt-3">{children}</div>
    </Card>
  );
}

function Rows({ entries }) {
  return (
    <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-[12px]">
      {entries.map(([label, value, testId]) => (
        <React.Fragment key={label}>
          <dt className="text-muted-foreground">{label}</dt>
          <dd className="text-right font-[600] break-words" data-testid={testId}>
            {value === null || value === undefined || value === "" ? "—" : String(value)}
          </dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

function Reveal({ label, testId, endpoint, assistId, render }) {
  const [reason, setReason] = useState("");
  const [data, setData] = useState(null);
  const run = useMutation({
    mutationFn: async () => (await api.post(
      `/admin/otter-assist/assists/${assistId}/reveal/${endpoint}`, { reason: reason.trim() })).data,
    onSuccess: (d) => { setData(d); toast.success("Revealed — recorded as a sensitive view."); },
    onError: (e) => toast.error(e?.response?.data?.detail === "assist_admin_capability_required"
      ? "You do not have permission for this." : "That didn't work."),
  });

  if (data) return render(data);

  return (
    <div className="space-y-2" data-testid={`oa-reveal-${testId}`}>
      <p className="text-[11px] text-muted-foreground">
        Hidden by default. Revealing this is recorded in the audit log with your reason — the content
        itself is never stored there.
      </p>
      <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
        placeholder="Why do you need to see this?" className="text-[12px]"
        data-testid={`oa-reveal-reason-${testId}`} />
      <Button size="sm" variant="outline" disabled={reason.trim().length < 3 || run.isPending}
        onClick={() => run.mutate()} data-testid={`oa-reveal-submit-${testId}`}>
        {run.isPending ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
          : <Eye className="mr-1.5 h-3.5 w-3.5" />}
        Reveal {label}
      </Button>
    </div>
  );
}

export default function OtterAssistAdminAssist() {
  const { assistId } = useParams();
  const qc = useQueryClient();
  const [dialog, setDialog] = useState(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["oa-admin-assist", assistId],
    queryFn: async () => (await api.get(`/admin/otter-assist/assists/${assistId}`)).data,
    retry: false,
  });
  const me = useQuery({
    queryKey: ["oa-admin-me"],
    queryFn: async () => (await api.get("/admin/otter-assist/me")).data,
    retry: false,
  });
  const audit = useQuery({
    queryKey: ["oa-admin-assist-audit", assistId],
    queryFn: async () => (await api.get(`/admin/otter-assist/assists/${assistId}/audit`)).data,
  });

  const caps = new Set(me.data?.capabilities || []);
  const assist = data?.assist;
  const paused = assist?.status === "admin_paused";
  const terminal = ["help_received", "closed", "expired", "cancelled",
                    "admin_cancelled"].includes(assist?.status);

  if (isLoading) return <Loading />;
  if (isError || !assist) {
    return <EmptyState icon={Ban} title="Assist not found"
      description="It may have been purged." testId="oa-admin-assist-missing" />;
  }

  return (
    <div className="space-y-4" data-testid="oa-admin-assist-detail">
      <Button asChild size="sm" variant="ghost" className="h-8 px-2 text-[12px]">
        <Link to="/dashboard/admin/otter-assist?view=active" data-testid="oa-admin-assist-back">
          <ArrowLeft className="mr-1 h-4 w-4" /><span>Back to Otter Assist</span>
        </Link>
      </Button>

      <Card className="p-4" data-testid="oa-admin-assist-header">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary" data-testid="oa-admin-assist-status">{assist.status}</Badge>
              <span className="text-[11px] text-muted-foreground">
                member sees: {assist.public_status}
              </span>
              {assist.report_count ? (
                <Badge variant="destructive">{assist.report_count} open report(s)</Badge>
              ) : null}
            </div>
            <p className="mt-2 max-w-2xl text-[13px]">{assist.summary || "—"}</p>
            <p className="mt-1 font-mono text-[10px] text-muted-foreground">{assist.assist_id}</p>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {!terminal && (paused ? (
              <Button size="sm" variant="outline" onClick={() => setDialog("resume")}
                data-testid="oa-admin-assist-resume">
                <Play className="mr-1.5 h-3.5 w-3.5" /> Resume
              </Button>
            ) : (
              <Button size="sm" variant="outline" onClick={() => setDialog("pause")}
                data-testid="oa-admin-assist-pause">
                <Pause className="mr-1.5 h-3.5 w-3.5" /> Pause
              </Button>
            ))}
            {!terminal && caps.has("cancel") && (
              <Button size="sm" variant="destructive" onClick={() => setDialog("cancel")}
                data-testid="oa-admin-assist-cancel">
                <Ban className="mr-1.5 h-3.5 w-3.5" /> Cancel
              </Button>
            )}
            {assist.post_id && (
              <Button asChild size="sm" variant="outline">
                <Link to={`/chatter/post/${assist.post_id}`} data-testid="oa-admin-assist-post">
                  View Post
                </Link>
              </Button>
            )}
          </div>
        </div>
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        <Section title="Sender & need" testId="oa-admin-assist-sender">
          <Rows entries={[
            ["Sender", assist.sender?.name, "oa-admin-assist-sender-name"],
            ["Email", assist.sender?.email],
            ["Posting identity", `${assist.posting_identity?.type || "—"}`],
            ["Urgency", assist.urgency],
            ["Deadline", assist.deadline_at?.slice(0, 10)],
            ["Activated", assist.activated_at?.slice(0, 16).replace("T", " ")],
            ["Post moderation", assist.post?.hidden ? "hidden" : (assist.post?.status || "active")],
            ["Detached from post", assist.detached_from_post ? "yes" : "no"],
          ]} />
        </Section>

        <Section title="Matching" testId="oa-admin-assist-matching">
          <Rows entries={[
            ["Run state", assist.matching?.run_state, "oa-admin-assist-run-state"],
            ["Scoring version", assist.matching?.scoring_version],
            ["Attempt", assist.matching?.attempt],
            [`Qualified (>= ${assist.matching?.qualified_min ?? 35})`,
              assist.matching?.qualified_count, "oa-admin-assist-qualified"],
            [`Strong (>= ${assist.matching?.strong_min ?? 60})`,
              assist.matching?.strong_count, "oa-admin-assist-strong"],
            ["Retrieval version", assist.matching?.retrieval_version],
            ["Completed", assist.matching?.completed_at?.slice(0, 16).replace("T", " ")],
          ]} />
        </Section>

        <Section title="Waves & delivery" testId="oa-admin-assist-delivery">
          <Rows entries={[
            ["Helpers offering", assist.responder_count, "oa-admin-assist-helpers"],
            ["Delivered", assist.delivered_count, "oa-admin-assist-delivered"],
            ["Queued", assist.deliveries?.queued],
            ...Object.entries(assist.deliveries?.by_state || {}).map(([k, v]) => [`state: ${k}`, v]),
          ]} />
          <ul className="mt-3 space-y-1 border-t border-border pt-2 text-[11px]">
            {(assist.waves || []).map((w) => (
              <li key={w.wave_number} className="flex justify-between gap-2"
                data-testid={`oa-admin-assist-wave-${w.wave_number}`}>
                <span>Wave {w.wave_number} · {w.state}</span>
                <span className="text-muted-foreground">
                  {w.scheduled_at?.slice(0, 16).replace("T", " ")}
                </span>
              </li>
            ))}
          </ul>
        </Section>

        {assist.viewer_role !== "analytics" && (
          <Section title="Refinements" testId="oa-admin-assist-refinements">
            <Rows entries={[
              ["Audience scope", assist.version?.audience_scope],
              ["Required", JSON.stringify(assist.version?.required_refinements || {})],
              ["Preferred", JSON.stringify(assist.version?.preferred_refinements || {})],
            ]} />
          </Section>
        )}
      </div>

      {caps.has("helper_note") && (
        <Section title="Helper messages" testId="oa-admin-assist-notes">
          <Reveal label="helper messages" testId="notes" endpoint="helper-notes" assistId={assistId}
            render={(d) => (d.helper_notes?.length ? (
              <ul className="space-y-2" data-testid="oa-revealed-notes">
                {d.helper_notes.map((n) => (
                  <li key={n.action_id} className="rounded-md border border-border p-2.5 text-[12px]">
                    <span className="font-[600]">{n.helper_name}</span>
                    <Badge variant="secondary" className="ml-2">{n.offer_state}</Badge>
                    <p className="mt-1 whitespace-pre-line">{n.helper_note}</p>
                  </li>
                ))}
              </ul>
            ) : <p className="text-[12px] text-muted-foreground">No helper messages on this Assist.</p>)} />
        </Section>
      )}

      {caps.has("recipient_identity") && (
        <Section title="Delivered recipients" testId="oa-admin-assist-recipients">
          <Reveal label="recipient identities" testId="recipients" endpoint="recipients"
            assistId={assistId}
            render={(d) => (
              <ul className="space-y-1 text-[12px]" data-testid="oa-revealed-recipients">
                {d.recipients?.length ? d.recipients.map((r) => (
                  <li key={r.delivery_id} className="flex flex-wrap justify-between gap-2">
                    <span><Users className="mr-1 inline h-3 w-3" />{r.name} · {r.email}</span>
                    <span className="text-muted-foreground">
                      {r.state} · wave {r.wave_number} · {r.seen_at ? "seen" : "unseen"}
                    </span>
                  </li>
                )) : <li className="text-muted-foreground">No deliveries yet.</li>}
              </ul>
            )} />
        </Section>
      )}

      {caps.has("candidates") && (
        <Section title="Candidate diagnostics" testId="oa-admin-assist-candidates">
          <Reveal label="candidate diagnostics" testId="candidates" endpoint="candidates"
            assistId={assistId}
            render={(d) => (
              <div data-testid="oa-revealed-candidates">
                <p className="text-[11px] italic text-muted-foreground">{d.thresholds?.note}</p>
                <ul className="mt-2 space-y-1 text-[12px]">
                  {d.candidates?.length ? d.candidates.map((c) => (
                    <li key={c.candidate_user_id}
                      className="border-b border-border/60 pb-1 last:border-0"
                      data-testid={`oa-candidate-${c.candidate_user_id}`}>
                      <div className="flex flex-wrap justify-between gap-2">
                        <span>{c.name}</span>
                        <span className="text-muted-foreground tabular-nums">
                          {c.match_quality_score} · {c.match_band || "—"} ·{" "}
                          {c.qualified ? `qualified (${c.qualified_via})`
                            : (c.disqualified_reason || "not qualified")}
                        </span>
                      </div>
                      <div className="mt-0.5 text-[11px] text-muted-foreground">
                        found via: {(c.retrieval_channels || []).join(", ") || "population scan"}
                        {" · "}query match: {(c.explicit_match_signals || []).join(", ") || "none"}
                        {c.explicit_match_tier ? ` · tier ${c.explicit_match_tier}` : ""}
                        {c.explicit_diagnostics?.sensitive_attributes_requested?.length
                          ? ` · requested ${c.explicit_diagnostics.sensitive_attributes_requested
                            .join(", ")}: ${c.explicit_diagnostics.sensitive_attributes_status}`
                          : ""}
                      </div>
                      <div className="mt-0.5 text-[11px] text-muted-foreground">
                        AI fit: {c.ai_fit_score ?? "—"}
                        {c.ai_fit_level ? ` (${c.ai_fit_level})` : ""}
                        {c.ai_fit_status && c.ai_fit_status !== "ok" ? ` [${c.ai_fit_status}]` : ""}
                        {c.ai_fit_reason_codes?.length
                          ? ` · ${c.ai_fit_reason_codes.join(", ")}` : ""}
                        {" · base "}{c.base_score ?? "—"}
                        {" + boosts "}{c.boost_total ?? 0}
                        {c.boosts_applied?.length
                          ? ` (${c.boosts_applied.map((b) => `${b.marker} +${b.points}`)
                            .join(", ")})` : ""}
                        {" = "}{c.match_quality_score}{" vs threshold "}{c.threshold_used ?? 35}
                        {c.legacy_v2_qualified === false
                          ? ` · legacy V2.1 would have rejected: ${c.legacy_v2_reason || "—"}`
                          : ""}
                      </div>
                    </li>
                  )) : <li className="text-muted-foreground">No candidates were produced.</li>}
                </ul>
              </div>
            )} />
        </Section>
      )}

      <Section title="History" testId="oa-admin-assist-history">
        <ul className="space-y-1 text-[11px]">
          {(audit.data?.lifecycle || []).map((row, i) => (
            <li key={`${row.created_at}-${i}`} className="flex flex-wrap gap-2">
              <span className="font-[600]">{row.to || row.action}</span>
              <span className="text-muted-foreground">
                {row.created_at?.slice(0, 16).replace("T", " ")} · by {row.actor_type}
              </span>
              {row.reason ? <span className="text-muted-foreground">— {row.reason}</span> : null}
            </li>
          ))}
          {!audit.data?.lifecycle?.length && (
            <li className="text-muted-foreground">No lifecycle history recorded.</li>
          )}
        </ul>
      </Section>

      <OaReasonDialog
        open={dialog === "pause"} onOpenChange={(o) => !o && setDialog(null)} testId="pause"
        title="Pause this Assist?" description="Outreach stops; nothing is lost."
        target={assistId} allowSilent
        effect="Stops all future outreach. The member sees it as Pending, exactly like their own pause. Everyone who already offered to help is unaffected."
        reversible confirmLabel="Pause Assist"
        invalidate={[["oa-admin-assist", assistId], ["oa-admin-assist-audit", assistId],
                     ["oa-admin-assists"]]}
        mutationFn={(body) => api.post(`/admin/otter-assist/assists/${assistId}/pause`, body)}
        onDone={() => { setDialog(null); qc.invalidateQueries({ queryKey: ["oa-admin-health"] }); }} />

      <OaReasonDialog
        open={dialog === "resume"} onOpenChange={(o) => !o && setDialog(null)} testId="resume"
        title="Resume this Assist?" description="Remaining outreach is rescheduled."
        target={assistId}
        effect="Unstarted waves are shifted by the length of the pause, so no wave fires late all at once."
        reversible confirmLabel="Resume Assist"
        invalidate={[["oa-admin-assist", assistId], ["oa-admin-assist-audit", assistId],
                     ["oa-admin-assists"]]}
        mutationFn={(body) => api.post(`/admin/otter-assist/assists/${assistId}/resume`, body)}
        onDone={() => setDialog(null)} />

      <OaReasonDialog
        open={dialog === "cancel"} onOpenChange={(o) => !o && setDialog(null)} testId="cancel"
        title="Cancel this Assist?" description="This is final." target={assistId}
        allowSilent destructive reversible={false} confirmLabel="Cancel Assist"
        effect="Ends all future Otter Assist activity. History and audit are kept, and the original Chatter post is NOT removed — moderate the post separately if it needs to come down."
        invalidate={[["oa-admin-assist", assistId], ["oa-admin-assist-audit", assistId],
                     ["oa-admin-assists"]]}
        mutationFn={(body) => api.post(`/admin/otter-assist/assists/${assistId}/cancel`, body)}
        onDone={() => setDialog(null)} />
    </div>
  );
}
